import React, { useState, useMemo, useEffect } from 'react';
import { useProjectToday, nowOn } from '../lib/projectToday';

/* ──────────────────────────────────────────────────────────────────
 * PrintScheduleModal — pick which date range to include on the
 * printed schedule sheet (one month / one quarter / custom range /
 * full project duration).
 *
 * 2026-05-28: warm-cream + sage token redesign so the modal matches
 * the rest of the redesigned chrome (Create Pay App / Release
 * Retainage / Compare / Task Properties). Same ModalShell-style
 * scrim + 14px-radius card; sage focus rings on selects; sage-soft
 * fills on selected radio cards; sage primary button on confirm.
 * ────────────────────────────────────────────────────────────────── */

// Helpers
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const ym = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

const startOfMonth = (yearMonth) => {
  const [y, m] = yearMonth.split('-').map(Number);
  return new Date(y, m - 1, 1);
};
const endOfMonth = (yearMonth) => {
  const [y, m] = yearMonth.split('-').map(Number);
  return new Date(y, m, 0); // day 0 of next month = last day of current
};
const startOfQuarter = (yearQ) => {
  const [y, q] = yearQ.split('-Q').map(Number);
  return new Date(y, (q - 1) * 3, 1);
};
const endOfQuarter = (yearQ) => {
  const [y, q] = yearQ.split('-Q').map(Number);
  return new Date(y, q * 3, 0);
};
const monthLabel = (yearMonth) => {
  const d = startOfMonth(yearMonth);
  return d.toLocaleString('default', { month: 'long', year: 'numeric' });
};
const quarterLabel = (yearQ) => {
  const [y, q] = yearQ.split('-Q');
  return `Q${q} ${y}`;
};

// Compute the list of months and quarters that overlap any task date range,
// so the dropdowns only offer ranges that actually contain tasks.
const buildMonthList = (minDate, maxDate) => {
  if (!minDate || !maxDate) return [];
  const out = [];
  const cur = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const end = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
  while (cur <= end) {
    out.push(ym(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return out;
};
const buildQuarterList = (minDate, maxDate) => {
  if (!minDate || !maxDate) return [];
  const out = [];
  const startQ = Math.floor(minDate.getMonth() / 3) + 1;
  const endQ = Math.floor(maxDate.getMonth() / 3) + 1;
  let y = minDate.getFullYear();
  let q = startQ;
  while (y < maxDate.getFullYear() || (y === maxDate.getFullYear() && q <= endQ)) {
    out.push(`${y}-Q${q}`);
    q += 1;
    if (q > 4) { q = 1; y += 1; }
  }
  return out;
};

/* ─────────────────────── Style tokens ─────────────────────── */

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 500,
  color: 'var(--text-3)', textTransform: 'uppercase',
  letterSpacing: '0.05em', marginBottom: 6,
};
const selectStyle = {
  height: 32, padding: '0 10px',
  fontSize: 14.5, color: 'var(--text-1)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 8,
  outline: 'none', cursor: 'pointer',
};
const onFocusRing = (e) => {
  e.currentTarget.style.borderColor = 'var(--accent)';
  e.currentTarget.style.boxShadow = '0 0 0 2px var(--accent-ring)';
};
const onBlurRing = (e) => {
  e.currentTarget.style.borderColor = 'var(--border-default)';
  e.currentTarget.style.boxShadow = 'none';
};
const ghostBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  height: 36, padding: '0 14px', borderRadius: 8,
  fontSize: 14.5, fontWeight: 500, color: 'var(--text-2)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  cursor: 'pointer',
};
const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  height: 36, padding: '0 16px', borderRadius: 8,
  fontSize: 14.5, fontWeight: 500, color: '#fff',
  background: 'var(--accent)',
  border: '1px solid color-mix(in oklch, var(--accent) 50%, black)',
  boxShadow: '0 1px 0 color-mix(in oklch, var(--accent) 35%, black), 0 1px 3px oklch(0.3 0.05 135 / 0.18)',
  cursor: 'pointer',
};

const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

/* ─────────────────────── Radio option card ─────────────────────── */

const OptionCard = ({ selected, onSelect, title, children }) => (
  <label
    onClick={onSelect}
    style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '12px 14px', marginBottom: 8,
      background: selected ? 'var(--accent-soft)' : 'var(--bg-surface)',
      border: `1px solid ${selected ? 'color-mix(in oklch, var(--accent) 35%, transparent)' : 'var(--border-subtle)'}`,
      borderRadius: 8,
      cursor: 'pointer',
      transition: 'background .12s, border-color .12s',
      boxShadow: selected ? '0 0 0 2px var(--accent-ring)' : 'none',
    }}
  >
    {/* Custom radio dot (sage when selected) */}
    <span
      aria-hidden="true"
      style={{
        width: 16, height: 16, borderRadius: 99,
        background: 'var(--bg-surface)',
        border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--border-default)'}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        marginTop: 2,
        transition: 'border-color .12s',
      }}
    >
      {selected && (
        <span style={{
          width: 8, height: 8, borderRadius: 99,
          background: 'var(--accent)',
        }} />
      )}
    </span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontSize: 15, fontWeight: 500,
        color: selected ? 'var(--accent-text)' : 'var(--text-1)',
      }}>{title}</div>
      {children}
    </div>
  </label>
);

/* ─────────────────────── Main ─────────────────────── */

const PrintScheduleModal = ({ open, onClose, onConfirm, projectStart, projectEnd }) => {
  const { testDate } = useProjectToday(); // a test job's test date picks the default month (2026-10-20)
  const [rangeType, setRangeType] = useState('full'); // 'month' | 'quarter' | 'custom' | 'full'

  const minDate = projectStart ? new Date(projectStart + 'T00:00:00') : null;
  const maxDate = projectEnd ? new Date(projectEnd + 'T00:00:00') : null;

  // eslint-disable-next-line
  const months = useMemo(() => buildMonthList(minDate, maxDate), [projectStart, projectEnd]);
  // eslint-disable-next-line
  const quarters = useMemo(() => buildQuarterList(minDate, maxDate), [projectStart, projectEnd]);

  // Default each sub-selection to something sensible
  const todayNow = nowOn(testDate);
  const todayYM = ym(todayNow);
  const todayYQ = `${todayNow.getFullYear()}-Q${Math.floor(todayNow.getMonth() / 3) + 1}`;
  const defaultMonth = months.includes(todayYM) ? todayYM : (months[0] || todayYM);
  const defaultQuarter = quarters.includes(todayYQ) ? todayYQ : (quarters[0] || todayYQ);

  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedQuarter, setSelectedQuarter] = useState(defaultQuarter);
  const [customFrom, setCustomFrom] = useState(months[0] || todayYM);
  const [customTo, setCustomTo] = useState(months[months.length - 1] || todayYM);

  // Months are formatted YYYY-MM, so lexical string compare is equivalent to
  // chronological compare. Keep customTo >= customFrom in both directions.
  useEffect(() => {
    if (customTo < customFrom) setCustomTo(customFrom);
  }, [customFrom, customTo]);

  // Esc closes (unless we're submitting — no submitting state here, this is
  // a pure picker that fires onConfirm synchronously).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Only offer months >= customFrom in the "to" dropdown.
  const customToOptions = useMemo(
    () => months.filter((m) => m >= customFrom),
    [months, customFrom],
  );

  if (!open) return null;

  const handleConfirm = () => {
    let from; let to; let label;
    if (rangeType === 'month') {
      from = ymd(startOfMonth(selectedMonth));
      to = ymd(endOfMonth(selectedMonth));
      label = monthLabel(selectedMonth);
    } else if (rangeType === 'quarter') {
      from = ymd(startOfQuarter(selectedQuarter));
      to = ymd(endOfQuarter(selectedQuarter));
      label = quarterLabel(selectedQuarter);
    } else if (rangeType === 'custom') {
      from = ymd(startOfMonth(customFrom));
      to = ymd(endOfMonth(customTo));
      label = `${monthLabel(customFrom)} – ${monthLabel(customTo)}`;
    } else {
      from = projectStart;
      to = projectEnd;
      label = 'Full Project Duration';
    }
    onConfirm({ from, to, label, rangeType });
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'oklch(0.2 0.008 100 / 0.28)',
        backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: 'max(80px, 10vh)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 500, maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 120px)',
          display: 'flex', flexDirection: 'column',
          background: 'var(--bg-surface)',
          borderRadius: 14,
          border: '1px solid var(--border-default)',
          boxShadow: 'var(--shadow-md), 0 24px 60px -12px oklch(0.2 0.01 100 / 0.18)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 20px 14px',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{
              margin: 0, fontSize: 17.5, fontWeight: 600,
              color: 'var(--text-1)', letterSpacing: '-0.018em',
            }}>Print Schedule</h2>
            <div style={{ fontSize: 13.5, color: 'var(--text-3)', marginTop: 3 }}>
              Pick which date range to include on the printed sheet.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 28, height: 28, borderRadius: 6,
              background: 'transparent', border: 'none',
              color: 'var(--text-3)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-3)'; }}
          >
            <XIcon />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '14px 20px 6px', overflowY: 'auto' }}>
          {/* One Month */}
          <OptionCard
            selected={rangeType === 'month'}
            onSelect={() => setRangeType('month')}
            title="One Month"
          >
            {rangeType === 'month' && (
              <div style={{ marginTop: 8 }}>
                <label style={labelStyle}>Month</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={selectStyle}
                  onFocus={onFocusRing} onBlur={onBlurRing}
                >
                  {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                </select>
              </div>
            )}
          </OptionCard>

          {/* One Quarter */}
          <OptionCard
            selected={rangeType === 'quarter'}
            onSelect={() => setRangeType('quarter')}
            title="One Quarter"
          >
            {rangeType === 'quarter' && (
              <div style={{ marginTop: 8 }}>
                <label style={labelStyle}>Quarter</label>
                <select
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={selectStyle}
                  onFocus={onFocusRing} onBlur={onBlurRing}
                >
                  {quarters.map((q) => <option key={q} value={q}>{quarterLabel(q)}</option>)}
                </select>
              </div>
            )}
          </OptionCard>

          {/* Custom Months */}
          <OptionCard
            selected={rangeType === 'custom'}
            onSelect={() => setRangeType('custom')}
            title="Custom Months"
          >
            {rangeType === 'custom' && (
              <div style={{
                marginTop: 8,
                display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8,
                alignItems: 'end',
              }}>
                <div>
                  <label style={labelStyle}>From</label>
                  <select
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ ...selectStyle, width: '100%' }}
                    onFocus={onFocusRing} onBlur={onBlurRing}
                  >
                    {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                  </select>
                </div>
                <span style={{
                  fontSize: 13, color: 'var(--text-3)',
                  paddingBottom: 8,
                }}>through</span>
                <div>
                  <label style={labelStyle}>To</label>
                  <select
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ ...selectStyle, width: '100%' }}
                    onFocus={onFocusRing} onBlur={onBlurRing}
                  >
                    {customToOptions.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                  </select>
                </div>
              </div>
            )}
          </OptionCard>

          {/* Full Project Duration */}
          <OptionCard
            selected={rangeType === 'full'}
            onSelect={() => setRangeType('full')}
            title="Full Project Duration"
          >
            {rangeType === 'full' && projectStart && projectEnd && (
              <div className="mono" style={{
                fontSize: 13, color: 'var(--text-3)', marginTop: 4,
                letterSpacing: '-0.02em',
              }}>
                {projectStart} <span style={{ color: 'var(--text-4)' }}>→</span> {projectEnd}
              </div>
            )}
          </OptionCard>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px 16px',
          background: 'var(--bg-surface-2)',
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
        }}>
          <button type="button" onClick={onClose} style={ghostBtn}>Cancel</button>
          <button type="button" onClick={handleConfirm} style={primaryBtn}>
            Open Print View
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrintScheduleModal;
